import { gql, useQuery } from '@apollo/client';
import IconBranch from '@apollo/icons/default/IconBranch.svg';
import IconCommit from '@apollo/icons/default/IconCommit.svg';
import IconDocument from '@apollo/icons/default/IconDocument.svg';
import IconGitHubSolid from '@apollo/icons/default/IconGitHubSolid.svg';
import IconSandbox from '@apollo/icons/default/IconSandbox.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconVariant from '@apollo/icons/default/IconVariant.svg';
import {
  Avatar,
  Button,
  Heading,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@apollo/orbit';
import classnames from 'classnames';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';

import { committerDisplayName } from 'src/app/shared/commiterDisplayName';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly/hooks';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { variantSettingsRouteConfig } from '../../routes';
import { CheckSidebar } from '../checkSidebar/CheckSidebar';
import { RerunBanner, RerunBannerCheckFragment } from '../RerunBanner';
import { TaskStatusBar, TaskStatusBarTaskFragment } from '../TaskStatusBar';

import {
  CheckDetailsConfiguration,
  CheckDetailsConfigurationWorkflowFragment,
} from './checkDetailsConfiguration/CheckDetailsConfiguration';

function BasicDetail({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={classnames(className, 'flex max-w-1/4 flex-col gap-2')}>
      <Heading size="lg" as="h3">
        {label}
      </Heading>
      {children}
    </div>
  );
}

export function CheckDetails() {
  const location = useLocation();
  const { currentCheckRoute, isProposalRoute } = useInternalGraphLinking();
  const { checkId, graphId } = useRouteParams(currentCheckRoute);
  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );

  const { data, loading, error } = useQuery<
    GraphQLTypes.CheckDetailsQuery,
    GraphQLTypes.CheckDetailsQueryVariables
  >(
    gql`
      query CheckDetailsQuery(
        $graphId: ID!
        $checkId: ID!
        $proposalsUseHasWarnings: Boolean!
      ) {
        service(id: $graphId) {
          id
          title
          checkWorkflow(id: $checkId) {
            id
            subgraphNames
            createdAt
            completedAt
            isSandboxCheck
            rerunOf {
              id
              ...RerunBannerCheckFragment
            }
            triggeredBy {
              id
              ... on User {
                fullName
                avatarUrl
              }
            }
            baseVariant {
              id
              name
            }
            gitContext {
              commit
              branch
              committer
              commitUrl
            }
            tasks {
              id
              ...TaskStatusBarTaskFragment
              ... on BuildCheckTask {
                id
                buildResult {
                  id
                  ... on CompositionBuildCheckResult {
                    compositionPackageVersion
                  }
                }
              }
            }
            ...CheckDetailsConfigurationWorkflowFragment
          }
        }
      }
      ${TaskStatusBarTaskFragment}
      ${CheckDetailsConfigurationWorkflowFragment}
      ${RerunBannerCheckFragment}
    `,
    {
      variables: { graphId, checkId, proposalsUseHasWarnings },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const graph = data?.service;
  const workflow = graph?.checkWorkflow;
  const compositionPackageVersion = useMemo(() => {
    const buildResult = workflow?.tasks.find(
      (
        t,
      ): t is Extract<
        typeof t,
        { __typename: 'CompositionCheckTask' | 'FilterCheckTask' }
      > =>
        t.__typename === 'CompositionCheckTask' ||
        t.__typename === 'FilterCheckTask',
    )?.buildResult;

    if (buildResult && 'compositionPackageVersion' in buildResult) {
      return buildResult.compositionPackageVersion;
    } else {
      return null;
    }
  }, [workflow]);

  if (loading) {
    return <Loading />;
  } else if (error) {
    return <ErrorPage error={error} />;
  } else if (!workflow || !graph) {
    return <NotFound />;
  }
  const type = workflow.isSandboxCheck
    ? 'sandbox'
    : workflow.tasks.some((t) => t.__typename === 'FilterCheckTask')
      ? 'contract'
      : 'normal';

  const duration = moment.duration(
    moment(workflow.completedAt).diff(workflow.createdAt),
  );
  return (
    <DocumentTitle title="Details">
      <div className="flex h-full">
        <CheckSidebar />
        <div className="w-0 flex-1 space-y-4 overflow-y-auto p-6">
          <RerunBanner
            graphId={graphId}
            checkId={checkId}
            rerunOfCheck={workflow.rerunOf}
          />
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <Heading size="2xl">Overview</Heading>
              <div className="flex flex-wrap gap-6">
                <BasicDetail label="Initiated">
                  <RelativeTimestamp value={workflow.createdAt} />
                </BasicDetail>
                {workflow.completedAt && (
                  <BasicDetail label="Duration">
                    {format.duration(duration)}
                  </BasicDetail>
                )}
                {type !== 'normal' && (
                  <BasicDetail label="Type">
                    {type === 'sandbox' ? (
                      <div className="flex items-center gap-2">
                        <IconSandbox className="icon-secondary shrink-0" />
                        Sandbox check
                      </div>
                    ) : type === 'contract' ? (
                      <div className="flex items-center gap-2">
                        <IconDocument />
                        Contract check
                      </div>
                    ) : (
                      assertUnreachable(type)
                    )}
                  </BasicDetail>
                )}
                {workflow.gitContext?.committer ? (
                  <BasicDetail label="Author">
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={committerDisplayName(
                          workflow.gitContext.committer,
                        )}
                        size="xs"
                      />
                      {committerDisplayName(workflow.gitContext.committer)}
                    </div>
                  </BasicDetail>
                ) : workflow.triggeredBy?.__typename === 'User' ? (
                  <BasicDetail label="Author">
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={workflow.triggeredBy.avatarUrl ?? undefined}
                        name={workflow.triggeredBy.fullName}
                        size="xs"
                      />
                      {workflow.triggeredBy.fullName}
                    </div>
                  </BasicDetail>
                ) : (
                  <BasicDetail label="Author">
                    <div className="flex items-center gap-2">
                      <Avatar size="xs" name="Unknown" />
                      Unknown
                    </div>
                  </BasicDetail>
                )}
                {workflow.gitContext?.branch && (
                  <BasicDetail label="Branch">
                    <div className="flex items-start gap-2">
                      <IconBranch className="icon-secondary mt-1 shrink-0" />
                      <span className="truncate break-all">
                        {workflow.gitContext.branch}
                      </span>
                    </div>
                  </BasicDetail>
                )}
                {workflow.gitContext?.commit && (
                  <BasicDetail label="Commit">
                    <div className="flex items-center gap-2">
                      {workflow.gitContext.commitUrl?.includes('github') ? (
                        <IconGitHubSolid />
                      ) : (
                        <IconCommit className="icon-secondary shrink-0" />
                      )}
                      {workflow.gitContext.commitUrl ? (
                        <ClickableText
                          as="a"
                          href={workflow.gitContext.commitUrl}
                          className="text-link hover:no-underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {workflow.gitContext.commit?.substring(0, 6)}
                        </ClickableText>
                      ) : (
                        workflow.gitContext.commit.substring(0, 7)
                      )}
                    </div>
                  </BasicDetail>
                )}
                {workflow.baseVariant?.name && (
                  <BasicDetail label="Variant">
                    <div className="flex items-center gap-2">
                      <IconVariant className="icon-secondary shrink-0" />
                      {workflow.baseVariant.name}
                    </div>
                  </BasicDetail>
                )}
                {isMinLengthArray(1, workflow.subgraphNames) && (
                  <BasicDetail label="Subgraph(s)">
                    {workflow.subgraphNames.length === 1 ? (
                      <div className="flex items-center gap-2">
                        <IconSingleService className="icon-secondary shrink-0" />
                        {workflow.subgraphNames[0]}
                      </div>
                    ) : (
                      <Popover placement="top">
                        <PopoverTrigger>
                          <Button variant="buttonLink">
                            <IconSingleService className="icon-secondary shrink-0" />
                            {workflow.subgraphNames[0]} +{' '}
                            {workflow.subgraphNames.length - 1} more
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="max-h-80 w-auto min-w-44">
                          <PopoverHeader>All subgraphs</PopoverHeader>
                          <PopoverBody className="space-y-1 overflow-y-auto">
                            {workflow.subgraphNames.map((subgraph) => (
                              <div
                                key={subgraph}
                                className="flex items-center gap-2"
                              >
                                <IconSingleService className="icon-secondary shrink-0" />
                                {subgraph}
                              </div>
                            ))}
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    )}
                  </BasicDetail>
                )}
                {compositionPackageVersion && (
                  <BasicDetail label="Composition">
                    Version {compositionPackageVersion}
                  </BasicDetail>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <Heading size="2xl">Tasks</Heading>
              <TaskStatusBar tasks={workflow.tasks} />
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex gap-1">
                <Heading size="2xl" className="inline-block">
                  Configuration{' '}
                </Heading>
                {!isProposalRoute && (
                  <Button
                    size="md"
                    className="ml-2"
                    variant="buttonLink"
                    leftIcon={<IconSettings />}
                    as={Link}
                    to={variantSettingsRouteConfig.locationFrom({
                      location,
                      fromRouteConfig: currentCheckRoute,
                      patch: { tab: 'checks' },
                    })}
                  >
                    Change
                  </Button>
                )}
              </div>
              <CheckDetailsConfiguration
                workflow={workflow}
                graphTitle={graph.title}
                graphId={graph.id}
              />
            </div>
          </div>
        </div>
      </div>
    </DocumentTitle>
  );
}
